import { Component, createEffect, onCleanup, onMount, Show, useContext } from 'solid-js';
import clsx from 'clsx';
import { ModalProps } from './modal-types';
import { Section } from '../../grid-system/section/section';
import { ErrorCatcher } from '../../tools/error-catcher';
import { CrossIcon } from '../../components/icons-library/cross';
import { isServer, Portal } from 'solid-js/web';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { lockPageScroll } from '../../tools/scroll-lock';

/* DS `ds-modal--sm|md|lg` max-widths, keyed by both the DS names and the
   pre-rebrand ones so existing call sites keep working. px, not rem: the DS scale is
   root-agnostic and the app runs at root=11px, so rem would diverge from the
   styleguide. `full` and an omitted size are absent on purpose — they fall through
   to 100%. */
const SIZE_MAX_WIDTH: Record<string, string> = {
    small: '424px',  sm: '424px',
    normal: '620px', md: '620px',
    large: '840px',  lg: '840px',
};

export const Modal: Component<ModalProps> = (props: ModalProps) => {
    let modalRef: HTMLDivElement | undefined;
    let releaseScrollLock: (() => void) | undefined;

    const { localize } = useContext(AppContext);

    const isAttention = () => props.type === 'attention';
    const capsToViewport = () => (props.overflow ?? 'container') === 'container';
    const centred = () => (props.align ?? 'center') === 'center';
    const attentionSlot = () => isAttention() && props.attentionIcon;

    /* The close button is absolute, so whichever block comes first has to carry the
       clearance for it; everything after starts flush. */
    const topPadding = (owner: 'attention' | 'header' | 'body') => {
        const first = attentionSlot() ? 'attention' : props.header ? 'header' : 'body';
        if (owner !== first) return 'pt-0';
        /* DS `--modal-padding` top: ds-space(12) for attention (ds-space(14) from 576px),
           ds-space(14) otherwise — which is also the clearance the close button needs. */
        return isAttention() ? 'pt-cvi-12 tablet:pt-cvi-14' : 'pt-cvi-14';
    };

    const alignClass = () => (centred() ? 'text-center' : 'text-left');

    /* Declared before the effects below: an effect can run synchronously (e.g. on an
       HMR re-render), and a `const` used before its line throws a TDZ ReferenceError. */
    const handleEscapePress = (e: KeyboardEvent) => {
        if (props.visible && e.key === 'Escape') {
            props.onClose();
        }
    };

    const cleanupEscapeListener = () => {
        document.removeEventListener('keydown', handleEscapePress);
    };

    onMount(() => {
        onCleanup(() => {
            cleanupEscapeListener();
        });
    });

    createEffect(() => {
        if (props.visible) {
            document.addEventListener('keydown', handleEscapePress);
        } else {
            cleanupEscapeListener();
        }
    });

    const handleModalClose = () => {
        props.onClose();
    };

    const handleOverlayClick = (e: MouseEvent) => {
        if (!modalRef?.contains(e.target as Node)) {
            handleModalClose();
            return;
        }
    };

    /* Page scroll is locked for as long as the modal is visible (see scroll-lock.ts). */
    createEffect(() => {
        if (props.visible) {
            releaseScrollLock ??= lockPageScroll();
        } else {
            releaseScrollLock?.();
            releaseScrollLock = undefined;
        }
    });

    onCleanup(() => {
        if (isServer) return;
        releaseScrollLock?.();
    });

    return (
        <ErrorCatcher componentName='Modal'>
            <Portal mount={document.getElementById('root')!}>
                <Show when={props.visible}>
                    <div
                        class={clsx(
                            '[container-type:inline-size] fixed overflow-y-scroll overflow-x-clip inset-0 pt-[10rem] z-10',
                            'max-mobile:pt-[5rem] [&::-webkit-scrollbar]:hidden',
                            /* DS `ds-modal::backdrop` — grey-950 at 80%. The old light-grey
                               scrim predates the rebrand. */
                            props.darkenBackground ? 'bg-cvi-bg-overlay-scrim' : 'bg-transparent',
                            /* DS `ds-modal--blur::backdrop`. */
                            props.blurBackdrop && 'backdrop-blur-[12px]',
                        )}
                        onClick={handleOverlayClick}
                    >
                        <Section
                            widthType={'bgFull'}
                            templateShorthand={[12]}
                            equalHeight={true}
                            removeSpacingBlock={true}
                        >
                            <div
                                ref={modalRef}
                                role="dialog"
                                aria-modal="true"
                                aria-label={props.ariaLabel}
                                class={clsx(
                                    'relative mx-auto flex w-full flex-col',
                                    /* The cap subtracts everything that sits above and below the panel:
                                       the overlay's own top offset (pt-[10rem], or pt-[5rem] under 480px)
                                       and the `Section`'s block padding on both edges. Counting the
                                       bottom edge is what leaves air under a full-height panel instead
                                       of it running into the viewport edge.
                                       `flex flex-col` here plus `min-h-0` on the body is what lets the
                                       body shrink below its content height and scroll. Both replace a
                                       flat `h-[calc(100svh-5rem)]`, which made every modal
                                       near-viewport-height whatever it contained. */
                                    /* `container` caps the panel and scrolls the body inside it;
                                       `page` lets the panel grow and leaves scrolling to the overlay. */
                                    capsToViewport() && [
                                        'max-h-[calc(100svh_-_10rem_-_(var(--cvi-section-space-close)_*_2))]',
                                        'max-mobile:max-h-[calc(100svh_-_5rem_-_(var(--cvi-section-space-close)_*_2))]',
                                    ],
                                    /* DS `ds-modal__container`: elevation-overlay surface, 8px radius,
                                       overlay shadow. `overflow-hidden` so the scrolling body's scrollbar
                                       is clipped to the radius instead of drawing over the corner. */
                                    'overflow-hidden bg-cvi-bg-elevation-overlay rounded-cvi-md shadow-elevation-overlay',
                                    /* DS entry transition (translateY + fade on open); the repo keyframe
                                       is the closest existing match to `ds-motion-easing-enter`. */
                                    'animate-fade-in-move-up motion-reduce:animate-none',
                                )}
                                style={{ 'max-width': SIZE_MAX_WIDTH[props.size ?? ''] || '100%' }}
                            >
                                {/* DS `ds-modal__close` — a ghost icon-button, absolute at ds-space(6)
                                    from the top-right corner. First in the DOM so it stays the first
                                    tabbable element for the TabHandler contract.
                                    The DS pairs its md icon-button with a 20px icon, but our `CrossIcon`
                                    art is inset well inside its own 20x20 viewBox (the glyph spans ~12px
                                    of it), so at 20px it renders visibly smaller than the DS close. `lg`
                                    puts the drawn X at roughly the DS size. */}
                                <button
                                    type="button"
                                    class="icon-button-ghost icon-button-ghost-md absolute top-cvi-6 right-cvi-6 z-10"
                                    onClick={handleModalClose}
                                    ref={(el: HTMLElement) => {
                                        props.closeButtonRef && props.closeButtonRef(el);
                                    }}
                                    aria-label={localize('close', 'Close')}
                                >
                                    <CrossIcon fill="currentColor" size="lg" />
                                </button>

                                {/* DS `ds-modal__attention` — centred icon block above the title,
                                    only on the attention type. */}
                                <Show when={attentionSlot()}>
                                    <div class={clsx('grid shrink-0 justify-center px-cvi-8 pb-cvi-8 tablet:px-cvi-10', topPadding('attention'))}>
                                        {props.attentionIcon}
                                    </div>
                                </Show>

                                {/* DS `ds-modal__header` — outside the scroll area, so a title stays
                                    put while the body scrolls under it. */}
                                <Show when={props.header}>
                                    <div class={clsx('shrink-0 px-cvi-8 tablet:px-cvi-10', topPadding('header'), alignClass())}>
                                        {props.header}
                                    </div>
                                </Show>

                                <div
                                    class={clsx(
                                        /* DS `--modal-padding`: ds-space(8) sides/bottom below 576px,
                                           ds-space(10) above it. */
                                        'px-cvi-8 pb-cvi-8 tablet:px-cvi-10 tablet:pb-cvi-10',
                                        topPadding('body'),
                                        /* Only the `container` mode scrolls here — in `page` mode the
                                           overlay owns the scrollbar and the body just grows. */
                                        capsToViewport() && 'min-h-0 overflow-y-auto',
                                        alignClass(),
                                        props.bodyClassName
                                    )}
                                >
                                    {props.children}
                                </div>
                            </div>
                        </Section>
                    </div>
                </Show>
            </Portal>
        </ErrorCatcher>
    );
};
